'use client'
import clsx from 'clsx'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useUserInfoProvider } from 'src/hooks/providers/UserInfoProvider'
import { Button } from 'src/components/Button'
import ErrorMessage from 'src/components/ErrorMessage'
import { TextField } from 'src/components/Fields'
import LoadingSpinner from 'src/components/LoadingSpinner'
import OauthButtons from 'src/components/OauthButtons'
import PasswordInput from 'src/components/PasswordField'
import type { OAuthKinds } from 'src/utils/api'
import { OAuthScopes } from 'src/utils/api'
import { authorize, getSiteUrlPrefix, isPasskeySupported, signin, signinWithPasskey, track } from 'src/utils/api'

type FormData = {
    emailAddress: string
    password: string
}

type PendingMethod = OAuthKinds | 'passkey' | 'password' | null

const SignInForm = () => {
    const [pendingMethod, setPendingMethod] = useState<PendingMethod>(null)
    const [error, setError] = useState<string | null>(null)
    const [passkeysAvailable, setPasskeysAvailable] = useState(false)
    const [formData, setFormData] = useState<FormData>({
        emailAddress: '',
        password: '',
    })
    const searchParams = useSearchParams()
    const returnTo = searchParams.get('return_to') ?? ''
    const code = searchParams.get('code')
    const allowSignup = searchParams.get('allow_signup') === 'true' && code !== null && code !== ''
    const { userInfo, userInfoLoading } = useUserInfoProvider()
    const isLoginPending = pendingMethod !== null

    useEffect(() => setPasskeysAvailable(isPasskeySupported()), [])

    useEffect(() => {
        if (!passkeysAvailable || PublicKeyCredential.isConditionalMediationAvailable === undefined) {
            return
        }
        let active = true
        PublicKeyCredential.isConditionalMediationAvailable()
            .then(async available => {
                if (!available || !active) {
                    return
                }
                const { redirectUrl } = await signinWithPasskey({ returnTo, conditional: true })
                if (active) {
                    window.location.href = redirectUrl ?? `${getSiteUrlPrefix()}/conversation/new`
                }
            })
            .catch(() => undefined)
        return () => {
            active = false
        }
    }, [passkeysAvailable, returnTo])

    useEffect(() => {
        if (!userInfoLoading && userInfo !== undefined) {
            window.location.replace(returnTo || `${getSiteUrlPrefix()}/conversation/new`)
        }
    }, [returnTo, userInfo, userInfoLoading])

    // Display OAuth error from URL params
    useEffect(() => {
        const oauthError = searchParams.get('oauth_error')
        const oauthEmail = searchParams.get('email')

        if (oauthError === 'account_not_found' && oauthEmail) {
            setError(`We could not find an account with the email ${decodeURIComponent(oauthEmail)}`)
        }
    }, [searchParams])

    const handleApiError = (err: any) => {
        setError(err instanceof Error ? 'Sorry, unable to find matching credentials' : 'An unknown error occurred')
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }))
    }

    const createOauthSignin = (kind: OAuthKinds, scopes: string[]) => {
        return async () => {
            setError(null)
            setPendingMethod(kind)
            await track('signinStarted', { kind }).catch(() => undefined)
            try {
                const { redirectUrl } = await authorize(kind, scopes, {
                    returnTo,
                    createAccountIfNotExists: allowSignup,
                })
                // we should be using Next's router.replace() however it does not reliably
                //  include cookies and have not invested enough time in configuring
                //  Next to fit our needs. this could potentially hide bugs but for now is OK.
                window.location.href = redirectUrl ?? `${getSiteUrlPrefix()}/conversation/new`
            } catch (error) {
                handleApiError(error)
            } finally {
                setPendingMethod(null)
            }
        }
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setPendingMethod('password')
        await track('signinStarted', { kind: 'email', emailAddress: formData.emailAddress }).catch(err => {
            // eat this error
        })
        try {
            const { redirectUrl } = await signin(formData.emailAddress, formData.password, { returnTo })
            // we should be using Next's router.replace() however it does not reliably
            //  include cookies and have not invested enough time in configuring
            //  Next to fit our needs. this could potentially hide bugs but for now is OK.
            window.location.href = redirectUrl ?? `${getSiteUrlPrefix()}/conversation/new`
        } catch (err) {
            handleApiError(err)
        } finally {
            setPendingMethod(null)
        }
    }

    const handlePasskeySignin = async () => {
        setError(null)
        setPendingMethod('passkey')
        await track('signinStarted', { kind: 'passkey' }).catch(() => undefined)
        try {
            const { redirectUrl } = await signinWithPasskey({ returnTo })
            window.location.href = redirectUrl ?? `${getSiteUrlPrefix()}/conversation/new`
        } catch (err) {
            if (err instanceof Error && err.name !== 'NotAllowedError') {
                handleApiError(err)
            }
        } finally {
            setPendingMethod(null)
        }
    }

    if (userInfoLoading || userInfo !== undefined) {
        return <LoadingSpinner isLoading>{null}</LoadingSpinner>
    }

    return (
        <div className="mt-8 mb-8 w-full">
            <p className="mb-3 text-xl text-neutral-900 font-bold">Sign in </p>
            <p className="mt-6 mb-3 text-xs text-neutral-900">Welcome back! Continue with:</p>
            {passkeysAvailable && (
                <Button
                    type="button"
                    variant="solid"
                    color="blue"
                    className="w-full !rounded bg-primary-500"
                    onClick={handlePasskeySignin}
                    disabled={isLoginPending}
                >
                    <LoadingSpinner isLoading={pendingMethod === 'passkey'} size={16}>
                        <span className="text-xs">Sign in with a passkey</span>
                    </LoadingSpinner>
                </Button>
            )}
            {passkeysAvailable && (
                <div className="relative flex py-3 items-center">
                    <div className="flex-grow h-px bg-gray-300"></div>
                    <div className="px-4 text-xs text-gray-500">Or</div>
                    <div className="flex-grow h-px bg-gray-300"></div>
                </div>
            )}
            <OauthButtons
                signinGitHub={createOauthSignin('github', OAuthScopes.github)}
                signinGitLab={createOauthSignin('gitlab', OAuthScopes.gitlab)}
                signinGoogle={createOauthSignin('google', OAuthScopes.google)}
                disabled={isLoginPending}
                pendingKind={
                    pendingMethod === 'google' || pendingMethod === 'github' || pendingMethod === 'gitlab'
                        ? pendingMethod
                        : undefined
                }
            />
            <div className="relative flex py-4 items-center">
                <div className="flex-grow h-px bg-gray-300"></div>
                <div className="px-4 text-xs text-gray-500">Or</div>
                <div className="flex-grow h-px bg-gray-300"></div>
            </div>
            <form method="POST" onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-4 mb-6">
                <div className="flex flex-col justify-center gap-y-4">
                    <TextField
                        label="Email"
                        name="emailAddress"
                        type="email"
                        placeholder="Enter your email"
                        autoComplete={passkeysAvailable ? 'email webauthn' : 'email'}
                        onChange={handleInputChange}
                        value={formData.emailAddress}
                        disabled={isLoginPending}
                        required
                    />
                    <div>
                        <PasswordInput
                            label="Password"
                            name="password"
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            onChange={handleInputChange}
                            value={formData.password}
                            disabled={isLoginPending}
                            required
                        />
                        <div className="col-span-full flex flex-row justify-between mt-3 px-1 text-sm">
                            <span className="flex-1" />
                            <Link
                                href="/forgot-password"
                                aria-disabled={isLoginPending}
                                className={clsx(
                                    'font-medium text-primary-700 text-xs hover:underline',
                                    isLoginPending && 'pointer-events-none opacity-50',
                                )}
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </div>
                </div>
                <div>
                    <Button
                        type="submit"
                        variant="solid"
                        color="blue"
                        className="bg-primary-500 w-full !rounded"
                        disabled={isLoginPending}
                    >
                        <LoadingSpinner isLoading={pendingMethod === 'password'} size={16}>
                            <span className="text-xs">Sign in with password</span>
                        </LoadingSpinner>
                    </Button>
                    <ErrorMessage>{error}</ErrorMessage>
                </div>
            </form>
            <p className="my-5 text-neutral-900 text-center text-xs">
                New to Circuitly?{' '}
                <Link
                    href="/signup"
                    aria-disabled={isLoginPending}
                    className={clsx(
                        'font-medium text-primary-700 text-xs hover:underline',
                        isLoginPending && 'pointer-events-none opacity-50',
                    )}
                >
                    Join the waitlist
                </Link>
            </p>
        </div>
    )
}

export default SignInForm
